import { BusinessTypesMapping } from 'api/client/mapping/business-types-mapping';
import { ISystemUserCountryRoleInfoModel, ITransferCountryUserModel, SystemUsersClient, TransferCountryUserModel } from 'api/swagger/ApiClient';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { UserContact } from 'model/business-types';
import { CountryCheckListItem, TransferUserRolesModel, UserCheckListItem } from 'model/forms/transfer-user-roles-model';
import { LookupsService } from 'services/lookups-service';
import { requireServices } from 'support/activation';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { EventHelper } from 'framework/aurelia_tools/event-helper';

@autoinject
export class AdminTransferUserRolesScreen {
  userId: number = null;
  transferFrom: UserContact = null;
  model: TransferUserRolesModel = null;
  backupData: ISystemUserCountryRoleInfoModel = null;
  route: RouteConfig = null;
  changed: boolean;
  transferButtonVisible: boolean;
  resetButtonVisible: boolean;

  constructor(private lookups: LookupsService, private systemUsersApi: SystemUsersClient, private businessTypesMapping: BusinessTypesMapping, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private eventHelper: EventHelper) {
    validationRenderer.allowedParents = 'bs-form-lookup';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.lookups);
  }

  async activate(params, route: RouteConfig) {
    this.userId = params?.id;
    this.route = route;
    this.initialize();
  }

  private async initialize() {
    if (this.userId != null)
      this.transferFrom = this.businessTypesMapping.mapUserDetailToUserContact(await this.systemUsersApi.getById(this.userId));
  }

  private async rebindModel() {
    await this.bindModel(this.transferFrom != null ? await this.systemUsersApi.getCountryUser(this.transferFrom.uniqueid) : null);
    this.validationController.reset();
  }

  private async bindModel(countryUserModel: ISystemUserCountryRoleInfoModel) {
    if (this.transferFrom != null) {
      const countryUserModel = await this.systemUsersApi.getCountryUser(this.transferFrom.uniqueid);
      this.model = {
        transferFrom: countryUserModel.user,
        countriesApprove: countryUserModel.countriesApprove.map(x => ({ country: x, checked: false } as CountryCheckListItem)),
        countriesContact: countryUserModel.countriesContact.map(x => ({ country: x, checked: false } as CountryCheckListItem)),
        delegateApprovers: countryUserModel.delegateApprovers.map(x => ({ user: x, checked: false } as UserCheckListItem)),
        transferTo: null
      } as TransferUserRolesModel;
    }
    else
      this.model = null;
    this.backupData = cloneDeep(countryUserModel);
  }

  usersLookupOptions = this.lookups.getUsersLookupOptions();

  async transfer() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async reset() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Reset your changes?', message: 'If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.bindModel(this.backupData);
    }
  }

  private async save() {
    await this.bindModel(await this.systemUsersApi.transferCountryUserRoles(this.model.transferFrom.id, new TransferCountryUserModel(this.getSaveData(this.model))));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('User roles has been successfully <b>transfered</b>.', 'Transfered', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: TransferUserRolesModel): ITransferCountryUserModel {
    return {
      countriesApproveIds: model.countriesApprove.filter(x => x.checked).map(x => x.country.id),
      countriesContactIds: model.countriesContact.filter(x => x.checked).map(x => x.country.id),
      delegateApproversIds: model.delegateApprovers.filter(x => x.checked).map(x => x.user.id),
      transferToUniqueId: model.transferTo.uniqueid
    }
  }

  @watch((x: AdminTransferUserRolesScreen) => x.transferFrom)
  private transferFromChanged() {
    this.rebindModel();
  }

  @watch((x: AdminTransferUserRolesScreen) => x.changed)
  private updateButons() {
    this.transferButtonVisible = this.changed;
    this.resetButtonVisible = this.changed;
  }

  @watch((x: AdminTransferUserRolesScreen) => x.model)
  private modelChanged() {
    this.validationController.reset();
  }

  @computedFrom('transferFrom')
  get userGroupLabel() {
    return this.transferFrom != null ?
      [`role: ${this.transferFrom.jobTitle ?? ''}`, `bp email: ${this.transferFrom.mail ?? ''}`, `countries email: ${this.transferFrom.countriesContactEmail ?? ''}`, `phone: ${this.transferFrom.phone ?? ''}`].filter(l => l != null && l !== '')
      : 'Transfer from';
  }
}
